/**
 * Ported from the lekterion codebase (`src/components/settings-menu.tsx`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */
import { Monitor, Moon, Languages, Settings, Sun, Volume2 } from "lucide-react";
import type { JSX } from "react";

import { useAutoplay } from "@/components/autoplay-provider";
import { useTargetLang } from "@/components/language-provider";
import { useTheme, type Theme } from "@/components/theme-provider";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@bindery/ui/components/ui/dropdown-menu";
import { TARGET_LANGS, type TargetLang } from "@/lib/i18n";
import { cn } from "@bindery/ui/lib/utils";

/**
 * Settings dropdown — the one home for the three global reader preferences:
 * theme (dark/light/system, ThemeProvider), translation target language
 * (LanguageProvider, §5+), and audio autoplay (AutoplayProvider, §10). Rendered
 * in both the reading-view header and the Library masthead; all three prefs are
 * app-wide and persist via localStorage, so the same menu works from either.
 */
export function SettingsMenu({
  triggerClassName,
}: {
  triggerClassName?: string;
}): JSX.Element {
  const { theme, setTheme } = useTheme();
  const { targetLang, setTargetLang } = useTargetLang();
  const { autoplay, setAutoplay } = useAutoplay();

  const currentLang =
    TARGET_LANGS.find((t) => t.code === targetLang)?.label ?? targetLang;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn("reader-head-action", triggerClassName)}
          aria-label="Settings"
        >
          <Settings className="size-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(v) => {
            setTheme(v as Theme);
          }}
        >
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4" />
            System
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4" />
            Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4" />
            Dark
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages className="size-4" />
            Language
            <span className="text-muted-foreground ml-auto normal-case">
              {currentLang}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={targetLang}
              onValueChange={(v) => {
                setTargetLang(v as TargetLang);
              }}
            >
              {TARGET_LANGS.map((t) => (
                <DropdownMenuRadioItem key={t.code} value={t.code}>
                  {t.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuCheckboxItem
          checked={autoplay}
          onCheckedChange={setAutoplay}
          // Keep the menu open so the toggle reads as a setting, not an action.
          onSelect={(e) => {
            e.preventDefault();
          }}
        >
          <Volume2 className="size-4" />
          Autoplay audio
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
